import * as GC from '@grapecity/spread-sheets';
import { IEmpyreanUser } from '@models/empyrean-user';
import { AccountCharacteristicConflict } from '@models/import/account-characteristic';
import { getDateString } from '@shared/dates';
import { SpreadContextMenu } from '@shared/spread/spread-context-menu';
import { colours, DASH, defaultBookOptions, FONT_NAME, FONT_SIZE, NUMBER_FORMATTER_2_DECIMALS } from '@shared/spread/spread-settings';

const headers = [
  { name: 'Account Characteristic ID', width: 130 },
  { name: 'Account ID', width: 150 },
  { name: 'Open Date', width: 100 },
  { name: 'Last EOP Date', width: 100 },
  { name: 'Existing SubProduct ID', width: 130 },
  { name: 'New SubProduct ID', width: 130 },
  { name: 'Existing Acquisition ID', width: 120 },
  { name: 'New Acquisition ID', width: 120 },
  { name: 'Existing Close Date', width: 100 },
  { name: 'New Close Date', width: 100 },
  { name: 'EOPBook', width: 100 },
  { name: 'Resolved by', width: 140 },
  { name: 'Resolved on', width: 100 }
];

export const populatePagedConflicts = (
  sheet: GC.Spread.Sheets.Worksheet,
  pagedConflicts: AccountCharacteristicConflict[],
  showOnlyActiveConflicts: boolean,
  usersArray: IEmpyreanUser[]
) => {
  sheet.suspendPaint();

  const users = {};
  usersArray.forEach(user => {
    if (!users[user.UserId]) {
      users[user.UserId] = `${user.FirstName} ${user.LastName}`;
    }
  });

  const arr = pagedConflicts
    .filter(p => !showOnlyActiveConflicts || (p.user_id_resolved === null && p.datetime_resolved === null))
    .map(p => [
      p.AccountCharacteristicID,
      p.AccountID,
      getDateString(p.OpenDate) || DASH,
      getDateString(p.LastEOPDate) || DASH,
      p.ExistingSubProductID,
      p.NewSubProductID,
      p.ExistingAcquisitionID || DASH,
      p.NewAcquisitionID || DASH,
      getDateString(p.ExistingCloseDate) || DASH,
      getDateString(p.NewCloseDate) || DASH,
      p.EOPBook,
      p.user_id_resolved ? users[p.user_id_resolved] || p.user_id_resolved : DASH,
      getDateString(p.datetime_resolved) || DASH
    ]);

  sheet.setRowCount(arr.length);
  setSheetStyling(sheet);

  sheet.setArray(0, 0, arr);

  // autofitColumns(sheet, 0, headers.length, 120, 500);

  sheet.resumePaint();
};

export const onWindowSizeChanged = (sheet: GC.Spread.Sheets.Worksheet, width: number) => {
  // if (sheet && width) {
  //   autofitColumns(sheet, 0, headers.length, 120, 500);
  // }
};

export const setUpWorkbook = (spread: GC.Spread.Sheets.Workbook, sheet: GC.Spread.Sheets.Worksheet) => {
  sheet.suspendPaint();
  sheet.suspendEvent();

  spread.options = { ...spread.options, ...defaultBookOptions };

  SpreadContextMenu.setSimpleCopy(spread);
  GC.Spread.Sheets.Commands.autoFitColumn.canUndo = false;

  // Sheet Options
  sheet.name('Conflicting Account Values');
  sheet.defaults.colWidth = 100;
  sheet.defaults.rowHeight = 20;
  sheet.options.allowCellOverflow = true;
  sheet.options.clipBoardOptions = GC.Spread.Sheets.ClipboardPasteOptions.values;
  sheet.options.colHeaderVisible = true;
  sheet.options.frozenlineColor = 'transparent';

  spread.options.allowExtendPasteRange = true;
  spread.options.cutCopyIndicatorBorderColor = '#2B495E';
  spread.options.cutCopyIndicatorVisible = true;
  spread.options.defaultDragFillType = GC.Spread.Sheets.Fill.AutoFillType.fillWithoutFormatting;

  // Sheet Protection Options
  sheet.options.isProtected = true;
  sheet.options.protectionOptions = {
    ...sheet.options.protectionOptions,
    allowSelectLockedCells: true,
    allowSelectUnlockedCells: true,
    allowSort: true,
    allowFilter: true,
    allowEditObjects: false,
    allowResizeColumns: true,
    allowResizeRows: false,
    allowDragInsertRows: false,
    allowDragInsertColumns: false,
    allowInsertRows: false,
    allowInsertColumns: false,
    allowDeleteRows: false,
    allowDeleteColumns: false
  };

  // Headers
  sheet.setColumnCount(headers.length);
  sheet.defaults.colWidth = 120;
  setHeadersStyling(sheet);

  headers.forEach((h, i) => {
    sheet.setColumnWidth(i, h.width);
    sheet.getCell(0, i, GC.Spread.Sheets.SheetArea.colHeader).text(h.name);
  });

  sheet.resumePaint();
  sheet.resumeEvent();
};

const setSheetStyling = (sheet: GC.Spread.Sheets.Worksheet) => {
  // Clear previous data and styling
  const fullRange = sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount());

  fullRange.clear(GC.Spread.Sheets.StorageType.style);
  fullRange.clear(GC.Spread.Sheets.StorageType.data);

  const hideRowFilter = new GC.Spread.Sheets.Filter.HideRowFilter(
    new GC.Spread.Sheets.Range(0, 0, sheet.getRowCount(), sheet.getColumnCount())
  );

  sheet.rowFilter(hideRowFilter);
  const eopbookIndex = headers.findIndex(h => h.name === 'EOPBook');
  sheet.getRange(0, eopbookIndex, sheet.getRowCount(), 1).formatter(NUMBER_FORMATTER_2_DECIMALS);

  headers.forEach((h, i) => {
    // Center dates
    if (h.name.toLowerCase().includes('date') || h.name.toLowerCase() === 'resolved on') {
      sheet.getRange(0, i, sheet.getRowCount(), 1).hAlign(GC.Spread.Sheets.HorizontalAlign.center);
    }
  });

  fullRange.vAlign(GC.Spread.Sheets.VerticalAlign.center).wordWrap(true);
  fullRange.wordWrap(true);
  fullRange.font(getFont());
  fullRange.cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`).vAlign(GC.Spread.Sheets.VerticalAlign.center);
  fullRange.locked(true);
};

const setHeadersStyling = (sheet: GC.Spread.Sheets.Worksheet) => {
  // Headers
  const colHeaderRange = sheet.getRange(0, 0, 1, sheet.getColumnCount(), GC.Spread.Sheets.SheetArea.colHeader);
  sheet.setRowHeight(0, 35, GC.Spread.Sheets.SheetArea.colHeader);
  colHeaderRange.foreColor(colours.MAIN).backColor(colours.MENU_BG);
  colHeaderRange.wordWrap(true);
  colHeaderRange.font(getFont());
  colHeaderRange.cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`).vAlign(GC.Spread.Sheets.VerticalAlign.center);

  const rowHeaderRange = sheet.getRange(0, 0, sheet.getRowCount(), 1, GC.Spread.Sheets.SheetArea.rowHeader);
  rowHeaderRange.font(getFont());
};

const getFont = () => `${parseInt(FONT_SIZE, 10) - 1}pt ${FONT_NAME}`;
